import { trussClientBase } from '../shared/constants';
import type { DashboardQueryRequest } from '@truss-security/truss-sdk-internal';

if (!trussClientBase) {
  console.warn('Missing Truss API base (/api/truss)');
}

const headers = {
  'Content-Type': 'application/json',
};

const API_URL = trussClientBase;

export interface PgQueryOptions {
  timeout?: number; // Timeout in milliseconds (default: 30000)
  retries?: number; // Number of retry attempts (default: 3)
  retryDelay?: number; // Initial retry delay in milliseconds (default: 1000)
}

/** Returns true if the error indicates the database server is unreachable (e.g. RDS down or network). */
export function isDatabaseUnreachableError(error: unknown): boolean {
  return !!(error as { isDatabaseUnreachable?: boolean })?.isDatabaseUnreachable;
}

/** After seeing "database unreachable", fail fast for this long instead of hitting the API again. */
const DATABASE_UNREACHABLE_COOLDOWN_MS = 30_000;
let databaseUnreachableUntil = 0;

function createDatabaseUnreachableError(): Error {
  const err = new Error('Database is temporarily unavailable. Please try again later or contact support.') as Error & { isDatabaseUnreachable?: boolean };
  err.isDatabaseUnreachable = true;
  return err;
}

/** Max concurrent pg-query requests to avoid overloading the backend/RDS. */
const PG_QUERY_CONCURRENCY = 4;
let pgQueryInFlight = 0;
const pgQueryQueue: Array<() => void> = [];

function acquirePgQuerySlot(): Promise<void> {
  if (pgQueryInFlight < PG_QUERY_CONCURRENCY) {
    pgQueryInFlight++;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    pgQueryQueue.push(() => {
      pgQueryInFlight++;
      resolve();
    });
  });
}

function releasePgQuerySlot(): void {
  pgQueryInFlight--;
  if (pgQueryQueue.length > 0) {
    const next = pgQueryQueue.shift()!;
    next();
  }
}

/**
 * Run a named dashboard SQL operation on the API (replaces raw /pg-query for pgService flows).
 */
export const dashboardQuery = async <T>(
  body: DashboardQueryRequest,
  options: PgQueryOptions = {},
): Promise<T> => {
  const {
    timeout = 30000,
    retries = 3,
    retryDelay = 1000,
  } = options;

  if (Date.now() < databaseUnreachableUntil) {
    throw createDatabaseUnreachableError();
  }

  await acquirePgQuerySlot();
  let lastError: Error | null = null;

  try {
    for (let attempt = 0; attempt <= retries; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        const response = await fetch(API_URL + '/dashboard/query', {
          method: 'POST',
          headers: {
            ...headers,
          },
          body: JSON.stringify(body),
          mode: 'cors',
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text();
          const err: any = new Error(`API request failed with status ${response.status}: ${errorText}`);
          err.status = response.status;

          if (response.status === 500 && errorText) {
            try {
              const parsed = JSON.parse(errorText) as { message?: string; error?: string };
              const message = parsed.message ?? parsed.error ?? errorText;
              if (
                typeof message === 'string' &&
                (message.includes("Can't reach database server") ||
                  message.includes('PrismaClientInitializationError'))
              ) {
                err.message =
                  'Database is temporarily unavailable. Please try again later or contact support.';
                err.isDatabaseUnreachable = true;
                databaseUnreachableUntil = Date.now() + DATABASE_UNREACHABLE_COOLDOWN_MS;
              }
            } catch {
              // ignore
            }
          }
          throw err;
        }

        return (await response.json()) as T;
      } catch (error: any) {
        clearTimeout(timeoutId);
        lastError = error;

        if (error.name === 'AbortError') {
          const timeoutError = new Error(
            `Query timed out after ${(timeout / 1000).toFixed(0)}s. The request may be too heavy; try a smaller date range or different filters.`,
          );
          (timeoutError as any).isTimeout = true;
          throw timeoutError;
        }

        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          error.message =
            'Request failed. The server may be busy or the connection was lost. Please try again.';
          (error as any).isNetworkOrTimeout = true;
        }

        if (error.status && error.status >= 400 && error.status < 500) {
          throw error;
        }

        if (error.isDatabaseUnreachable) {
          throw error;
        }

        if (attempt < retries) {
          const delay = retryDelay * Math.pow(2, attempt);
          console.warn(`Dashboard query attempt ${attempt + 1} failed, retrying in ${delay}ms...`, error.message);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        console.error('Fetch error after all retries:', error);
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          error.message =
            'Request failed. The server may be busy or the connection was lost. Please try again.';
          (error as any).isNetworkOrTimeout = true;
        }
        throw error;
      }
    }

    throw lastError || new Error('Dashboard query failed after all retries');
  } finally {
    releasePgQuerySlot();
  }
};

export const callAdminApi = async <T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: any
): Promise<T> => {

  const dashboardHeaders = {
    'Content-Type': 'application/json',
  };

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method,
      headers: dashboardHeaders,
      body: body ? JSON.stringify(body) : undefined,
      mode: 'cors',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed with status ${response.status}: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Fetch error:', error);
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      console.error('This error often occurs due to CORS issues or network problems.');
      console.error('Please check your network connection and ensure the API endpoint is correct and accessible.');
    }
    throw error;
  }
};

// ==============================================
// NEW PRISMA-BASED PRODUCT ENDPOINTS
// ==============================================

export const getProductBatch = async (
  ids: number[], 
  page: number = 1, 
  pageSize: number = 20
): Promise<{
  products: any[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}> => {
  
  try {
    const requestBody = { ids, page, pageSize };
    
    const response = await fetch(`${API_URL}/products/batch`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify(requestBody),
    });


    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed with status ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('getProductBatch ERROR:', error);
    throw error;
  }
};
